import React from 'react';
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import "../styles/mainpage-styles.css";

interface GenericPdfDownloaderProps {
    rootElementId: string;
    downloadFileName: string;
}

const A4_WIDTH_MM  = 210;
const A4_HEIGHT_MM = 297;

const GenericPdfDownloader = (props: GenericPdfDownloaderProps) => {
    const date = new Date();
    const downloaddate = [date.getFullYear(), date.getMonth() + 1, date.getDate()].join('-');

    const CreatePDFfromHTML = async () => {
        const input = document.getElementById(props.rootElementId);
        if (!input) return;

        // Temporarily remove the CSS transform so html2canvas captures at full
        // 800px resolution regardless of the current screen scale.
        const prevTransform   = input.style.transform;
        const prevMarginLeft  = input.style.marginLeft;
        const prevWidth       = input.style.width;
        input.style.transform  = 'none';
        input.style.marginLeft = '0';
        // width may be set as a number via inline style — ensure px unit
        if (!input.style.width) input.style.width = '800px';

        const canvas = await html2canvas(input, {
            scale: 2,           // 2× for crisp text/images
            useCORS: true,
            logging: false,
            width:  800,
            windowWidth: 800,
        });

        // Restore transform
        input.style.transform  = prevTransform;
        input.style.marginLeft = prevMarginLeft;
        input.style.width      = prevWidth;

        const imgData   = canvas.toDataURL('image/png');
        const pxWidth   = canvas.width;
        const pxHeight  = canvas.height;

        // How many mm per pixel (using A4 width as reference)
        const mmPerPx   = A4_WIDTH_MM / pxWidth;
        const totalH_mm = pxHeight * mmPerPx;

        const pdf = new jsPDF('p', 'mm', 'a4');

        if (totalH_mm <= A4_HEIGHT_MM) {
            // Content fits on one page — centre it vertically with padding
            const topPad = (A4_HEIGHT_MM - totalH_mm) / 2;
            pdf.addImage(imgData, 'PNG', 0, topPad, A4_WIDTH_MM, totalH_mm);
        } else {
            // Multi-page: slice the image into A4-height chunks
            const pageH_px = Math.floor(A4_HEIGHT_MM / mmPerPx);
            let offsetY_px = 0;
            let pageIndex  = 0;

            while (offsetY_px < pxHeight) {
                const sliceH_px = Math.min(pageH_px, pxHeight - offsetY_px);

                // Draw only the slice for this page onto a temp canvas
                const pageCanvas = document.createElement('canvas');
                pageCanvas.width  = pxWidth;
                pageCanvas.height = sliceH_px;
                const ctx = pageCanvas.getContext('2d')!;
                ctx.drawImage(canvas, 0, offsetY_px, pxWidth, sliceH_px, 0, 0, pxWidth, sliceH_px);

                const pageImg   = pageCanvas.toDataURL('image/png');
                const sliceH_mm = sliceH_px * mmPerPx;

                if (pageIndex > 0) pdf.addPage();
                pdf.addImage(pageImg, 'PNG', 0, 0, A4_WIDTH_MM, sliceH_mm);

                offsetY_px += pageH_px;
                pageIndex++;
            }
        }

        pdf.save(props.downloadFileName + downloaddate + '.pdf');
    };

    return (
        <button className="generic-button hover-up" onClick={CreatePDFfromHTML}>
            <SaveAltIcon />PDF
        </button>
    );
};

export default GenericPdfDownloader;
